import { FC, useEffect, useMemo, useState } from 'react';

import { Check, Sparkles } from 'lucide-react';

import { PremiumBenefits } from './PremiumBenefits';
import { PremiumPackageCard } from './PremiumPackageCard';
import { PurchasedPremiumServices } from './PurchasedPremiumServices';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../../components/ui/Dialog';
import {
    ActivePremiumService,
    PremiumPackage,
    PremiumSummary,
} from '../../../../types';
import { createPremiumCheckout } from '../../premiumRequests';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    premiumPackages: PremiumPackage[] | null;
    activePremiumServices: ActivePremiumService[] | null;
    premiumSummary: PremiumSummary;
}

export const PremiumDetailsDialog: FC<Props> = ({
    open,
    onOpenChange,
    premiumPackages,
    activePremiumServices,
    premiumSummary,
}) => {
    const premiumPackageList = premiumPackages ?? [];
    const activePremiumServiceList = activePremiumServices ?? [];
    const [loadingServiceId, setLoadingServiceId] = useState<number | null>(
        null,
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const highestUnitPrice = useMemo(
        () =>
            Math.max(
                ...premiumPackageList.map(
                    (premiumPackage) =>
                        premiumPackage.price / premiumPackage.premium_ads_count,
                ),
                0,
            ),
        [premiumPackageList],
    );
    const bestValuePackageId = useMemo(() => {
        if (premiumPackageList.length < 2) {
            return null;
        }

        return premiumPackageList.reduce((bestPackage, premiumPackage) => {
            const unitPrice =
                premiumPackage.price / premiumPackage.premium_ads_count;
            const bestUnitPrice =
                bestPackage.price / bestPackage.premium_ads_count;

            return unitPrice < bestUnitPrice ? premiumPackage : bestPackage;
        }).id;
    }, [premiumPackageList]);

    useEffect(() => {
        if (open) {
            setHistoryOpen(false);
        }
    }, [open]);

    const startCheckout = async (serviceId: number) => {
        setLoadingServiceId(serviceId);
        setErrorMessage(null);

        try {
            const paymentUrl = await createPremiumCheckout(serviceId);
            window.location.assign(paymentUrl);
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Не вдалося створити оплату.',
            );
            setLoadingServiceId(null);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (loadingServiceId === null) {
                    onOpenChange(nextOpen);
                }
            }}
        >
            <DialogContent
                className="top-0 right-0 bottom-0 left-0 h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-y-auto rounded-none border-0 p-0 sm:top-1/2 sm:right-auto sm:bottom-auto sm:left-1/2 sm:h-auto sm:max-h-[92vh] sm:w-[calc(100vw-2rem)] sm:max-w-6xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border"
                data-testid="premium-details-dialog"
            >
                <DialogHeader className="border-b px-5 py-4 pr-12 sm:px-6">
                    <DialogTitle className="flex items-center gap-2 text-left text-xl">
                        <Sparkles className="h-5 w-5 text-primary" />
                        Premium пропозиції
                    </DialogTitle>
                    <DialogDescription className="text-left">
                        Керуйте активаціями, переглядайте придбані набори та
                        обирайте новий пакет для своїх пропозицій.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 px-5 py-5 sm:px-6">
                    <section
                        className="grid gap-5 rounded-2xl border border-primary/20 bg-primary/5 p-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-center"
                        data-testid="premium-overview-layout"
                    >
                        <div>
                            <h2 className="max-w-xl text-2xl font-bold tracking-tight">
                                Зробіть найкращі пропозиції помітнішими
                            </h2>
                            <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
                                Premium дає пропозиції пріоритет у каталозі,
                                окрему premium-сторінку та помітне виділення на
                                сторінці товару.
                            </p>

                            <div className="mt-5 flex items-center gap-2 text-sm text-muted-foreground">
                                <Check className="h-4 w-4 text-green-600" />
                                Одна активація діє 7 днів
                            </div>
                        </div>

                        <div
                            className="rounded-2xl border bg-background/90 p-4 shadow-sm"
                            data-testid="premium-balance"
                        >
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <h3 className="text-sm font-semibold">
                                    Баланс активацій
                                </h3>
                                <Sparkles className="h-5 w-5 text-primary/70" />
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="rounded-xl bg-green-500/10 px-2 py-3">
                                    <div
                                        className="text-xl font-bold text-green-700 dark:text-green-400"
                                        data-testid="premium-available-slots"
                                    >
                                        {premiumSummary.availableSlotsCount}
                                    </div>
                                    <div className="mt-1 text-[11px] text-muted-foreground">
                                        доступно
                                    </div>
                                </div>
                                <div className="rounded-xl bg-blue-500/10 px-2 py-3">
                                    <div className="text-xl font-bold text-blue-700 dark:text-blue-400">
                                        {premiumSummary.activeSlotsCount}
                                    </div>
                                    <div className="mt-1 text-[11px] text-muted-foreground">
                                        активно
                                    </div>
                                </div>
                                <div className="rounded-xl bg-muted px-2 py-3">
                                    <div className="text-xl font-bold">
                                        {premiumSummary.usedSlotsCount}
                                    </div>
                                    <div className="mt-1 text-[11px] text-muted-foreground">
                                        використано
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        id="premium-package-selection"
                        className="scroll-mt-6"
                        aria-labelledby="premium-packages-title"
                        data-testid="premium-package-selection"
                    >
                        <div className="mb-4">
                            <h2
                                id="premium-packages-title"
                                className="text-xl font-semibold"
                            >
                                Купити активації
                            </h2>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Оберіть потрібну кількість. Активації можна
                                використати для будь-якої вашої пропозиції.
                            </p>
                        </div>

                        {errorMessage && (
                            <div
                                className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
                                role="alert"
                            >
                                {errorMessage}
                            </div>
                        )}

                        {premiumPackageList.length > 0 ? (
                            <div className="grid gap-4 md:grid-cols-3">
                                {premiumPackageList.map((premiumPackage) => (
                                    <PremiumPackageCard
                                        key={premiumPackage.id}
                                        premiumPackage={premiumPackage}
                                        highestUnitPrice={highestUnitPrice}
                                        recommended={
                                            premiumPackage.id ===
                                            bestValuePackageId
                                        }
                                        loadingServiceId={loadingServiceId}
                                        onCheckout={startCheckout}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-dashed px-5 py-8 text-center text-sm text-muted-foreground">
                                Наразі немає доступних premium-наборів.
                            </div>
                        )}
                    </section>

                    <PremiumBenefits />

                    <PurchasedPremiumServices
                        activePremiumServices={activePremiumServiceList}
                        open={historyOpen}
                        onOpenChange={setHistoryOpen}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};
